import React, { useState, useEffect } from 'react';
import LocationSelector from './LocationSelector';
import ServiceSelector from './ServiceSelector';
import PractitionerSelector from './PractitionerSelector';
import DateSelector from './DateSelector';
import TimeSlotSelector from './TimeSlotSelector';
import BookingForm from './BookingForm';
import BookingConfirmation from './BookingConfirmation';
import './BookingPage.scss';

const BookingPage = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [timeZone, setTimeZone] = useState('(GMT+08:00) Perth');
  const [availabilityPeriod, setAvailabilityPeriod] = useState(null);
  const [bookingData, setBookingData] = useState(null);

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setSelectedService(null);
    setSelectedPractitioner(null);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setStep(2);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setStep(3);
  };

  const handlePractitionerSelect = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setStep(4);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
    setStep(5);
  };

  const handleTimeZoneChange = (tz) => {
    setTimeZone(tz);
  };

  const handleTimeSlotSelect = (timeSlot, period) => {
    setSelectedTimeSlot(timeSlot);
    setAvailabilityPeriod(period);
    setStep(6);
  };

  const handleFormSubmit = (formData) => {
    setBookingData({
      ...formData,
      appointmentDate: selectedDate,
      timeSlot: selectedTimeSlot,
      location: selectedLocation,
      service: selectedService,
      practitioner: selectedPractitioner,
      timeZone: timeZone,
      availabilityPeriod: availabilityPeriod,
    });
    setStep(7);
  };

  const handleGoToStep = (targetStep) => {
    if (targetStep === 5) {
      setSelectedTimeSlot(null);
      setAvailabilityPeriod(null);
    }
    setStep(targetStep);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const getStepLabel = (stepNum) => {
    const labels = {
      1: 'Location',
      2: 'Service',
      3: 'Practitioner',
      4: 'Date',
      5: 'Time',
      6: 'Details',
      7: 'Confirm',
    };
    return labels[stepNum] || '';
  };

  return (
    <div className="booking-page">
      <div className="booking-container">
        <div className="booking-header">
          <h1>Book an Appointment</h1>
          <p>Complete the steps below to schedule your appointment</p>
        </div>

        <div className="booking-steps">
          {[1, 2, 3, 4, 5, 6, 7].map((stepNum) => (
            <div key={stepNum} className={`step ${step >= stepNum ? 'active' : ''} ${step === stepNum ? 'current' : ''}`}>
              <span className="step-number">{stepNum}</span>
              <span className="step-label">{getStepLabel(stepNum)}</span>
            </div>
          ))}
        </div>

        <div className="booking-content">
          {step === 1 && (
            <LocationSelector
              onLocationSelect={handleLocationSelect}
              selectedLocation={selectedLocation}
            />
          )}
          {step === 2 && (
            <ServiceSelector
              onServiceSelect={handleServiceSelect}
              selectedService={selectedService}
              selectedLocation={selectedLocation}
            />
          )}
          {step === 3 && (
            <PractitionerSelector
              onPractitionerSelect={handlePractitionerSelect}
              selectedPractitioner={selectedPractitioner}
              selectedLocation={selectedLocation}
            />
          )}
          {step === 4 && (
            <DateSelector
              onDateSelect={handleDateSelect}
              selectedDate={selectedDate}
              location={selectedLocation}
              practitioner={selectedPractitioner}
              timeZone={timeZone}
              onTimeZoneChange={handleTimeZoneChange}
            />
          )}
          {step === 5 && (
            <TimeSlotSelector
              date={selectedDate}
              onTimeSlotSelect={handleTimeSlotSelect}
              selectedTimeSlot={selectedTimeSlot}
              onBack={handleBack}
              location={selectedLocation}
              practitioner={selectedPractitioner}
            />
          )}
          {step === 6 && (
            <BookingForm
              onSubmit={handleFormSubmit}
              onBack={handleBack}
              selectedDate={selectedDate}
              selectedTimeSlot={selectedTimeSlot}
              selectedService={selectedService}
              selectedLocation={selectedLocation}
              selectedPractitioner={selectedPractitioner}
              timeZone={timeZone}
              availabilityPeriod={availabilityPeriod}
            />
          )}
          {step === 7 && (
            <BookingConfirmation
              bookingData={bookingData}
              onClose={onClose}
              onGoToStep={handleGoToStep}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
